import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom-v5-compat';

import API from 'api/API';
import CenteredContainer from 'components/layouts/containers/CenteredContainer/CenteredContainer';
import { useUserProfile } from 'context/AuthContext';

import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const STRIPE_PUBLIC_KEY =
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'SYSTEM ERROR: SET .env.XXX.REACT_APP_STRIPE_PUBLIC_KEY';
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

interface PaymentStatusProps {
  clientSecret: string;
}

// We need a separate component to handle the payment status processing
// because we need to use the `useStripe` hook which can only be used inside
// a component wrapped in `<Elements>`.
const PaymentStatusProcessor = (props: PaymentStatusProps) => {
  const { clientSecret } = props;
  const stripe = useStripe();
  const [message, setMessage] = useState('Redirecting to home page...');
  const { setUserProfile } = useUserProfile();

  const navigate = useNavigate();
  useEffect(() => {
    if (!stripe) {
      return;
    }

    // Retrieve the SetupIntent
    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
      // Inspect the SetupIntent `status` to indicate the status of the payment
      // to your customer.
      //
      // Some payment methods will [immediately succeed or fail][0] upon
      // confirmation, while others will first enter a `processing` state.
      //
      // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
      if (setupIntent === undefined) {
        setMessage('Failed to retrieve payment status. Please try again.');
        return;
      }
      switch (setupIntent.status) {
        case 'succeeded':
          setMessage('Success! Your payment method has been saved.');
          // Successful payment setup, we can now unpaywall the user
          const api = new API();
          api
            .post('/api/unpaywall_company', { payment_method_id: setupIntent.payment_method })
            .then(() => {
              // We now need to update the user profile to reflect the unpaywalling
              api
                .get('api/user_profile')
                .then((response) => {
                  setUserProfile(response.data.user_profile);
                })
                .then(() => {
                  // Redirect to home page after successful unpaywalling
                  navigate('/');
                })
                .catch((e) => {
                  // Silently eat error. The user will need to refresh the page
                  // to see the updated profile.
                });
            })
            .catch(() => {
              // Handle error if unpaywalling fails
              setMessage('Failed to unpaywall the company. Please contact support.');
            });
          break;

        case 'processing':
          setMessage("Processing payment details. We'll update you when processing is complete.");
          break;

        case 'requires_payment_method':
          // Redirect your user back to your payment page to attempt collecting
          // payment again
          setMessage('Failed to process payment details. Please try another payment method.');
          navigate('/payment');
          break;
      }
    });
  }, [stripe, clientSecret, navigate, setUserProfile]);

  return <CenteredContainer>{message}</CenteredContainer>;
};

const PaymentStatus = () => {
  // Retrieve the "setup_intent_client_secret" query parameter appended to
  // your return_url by Stripe.js
  const clientSecret =
    new URLSearchParams(window.location.search).get('setup_intent_client_secret') || '';

  const options = {
    // passing the SetupIntent's client secret
    clientSecret: clientSecret,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentStatusProcessor clientSecret={clientSecret} />
    </Elements>
  );
};

export default PaymentStatus;
